<template>
  <div>
    <div class="page-title"><span v-html="AddOracleSvg" />Build an oracle</div>
    <div class="deploy-wrapper">
      <div
        class="sidebar"
        :class="{ opened: deployerStore.summaryMobileOpened }"
      >
        <DeployerDeploySummary
          :oracle-values="oracleValues"
          :can-deploy="canDeploy"
          :is-payment-processed="isPaymentProcessed"
          @deploy="deployOracle"
          @pay="payDeployment"
          @remove-feed="removeFeed"
          @clear-all="clearFeed"
        />
      </div>

      <div class="deploy">
        <div class="names-row">
          <DeployerUiFormInputText
            v-model="oracleValues.name"
            :label="texts.settings.fields[6].label"
            :placeholder="texts.settings.fields[6].placeholder"
            :tooltip="texts.settings.fields[6].tooltip"
          />
          <div class="team">
            <DeployerUiFormLabel
              :label="texts.settings.fields[7].label"
              :tooltip="texts.settings.fields[7].tooltip"
            />
            <DeployerUiTeamName />
          </div>
        </div>

        <DeployerUiBox
          :box-title="texts.settings.title"
          :box-text="texts.settings.text"
        >
          <div v-if="showChainRpc" class="settings-row">
            <DeployerUiFormInputText
              v-model="oracleValues.chainId"
              :svg-icon="NetworkSvg"
              :label="texts.settings.fields[0].label"
              :placeholder="texts.settings.fields[0].placeholder"
              :tooltip="texts.settings.fields[0].tooltip"
              @input="debouncedValidateChainRPC"
            />
            <DeployerUiFormInputText
              v-model="oracleValues.rpc_url"
              :svg-icon="RpcSvg"
              :label="texts.settings.fields[1].label"
              :placeholder="texts.settings.fields[1].placeholder"
              :tooltip="texts.settings.fields[1].tooltip"
              :fullwidth="true"
              @input="debouncedValidateChainRPC"
            />
          </div>

          <div class="chain-validation">
            <span v-if="chainRPCValidation.loading" v-html="Loading" />
            <p
              v-for="(message, index) in chainRPCValidation.messages"
              :key="index"
              :class="{ invalid: !chainRPCValidation.isValid }"
              class="validation-message"
            >
              <span
                v-if="chainRPCValidation.isValid"
                class="icon"
                v-html="Check"
              />
              <span v-else class="icon" v-html="X" />
              <span>{{ message }}</span>
            </p>
          </div>

          <hr v-if="showChainRpc" />

          <div class="settings-row">
            <DeployerUiFormAssetSelect
              v-model="oracleValues.selectedAssets"
              :feeds-available="feedsAvailable"
              @show-asset-list-changed="
                (value) => {
                  console.log('show-asset-list-changed', value)
                  showFeedsErrors = value
                }
              "
            />
          </div>

          <div class="settings-row">
            <DeployerUiFormInputText
              v-model="oracleValues.heartbeat"
              input-type="number"
              :error="errors.heartbeat"
              :label="texts.settings.fields[3].label"
              :placeholder="texts.settings.fields[3].placeholder"
              :tooltip="texts.settings.fields[3].tooltip"
              :select-values="['sec', 'min', 'hr']"
              :selected-value="oracleValues.heartbeatUnit"
              @select-value="oracleValues.heartbeatUnit = $event"
            />
            <DeployerUiFormInputText
              v-model="oracleValues.deviation"
              input-type="number"
              :error="errors.deviation"
              :label="texts.settings.fields[4].label"
              :placeholder="texts.settings.fields[4].placeholder"
              :tooltip="texts.settings.fields[4].tooltip"
              :select-values="['%']"
            />
          </div>
          <template v-if="whiteLabel?.network !== 'raas'">
            <DeployerUiFormLabel
              :label="texts.settings.fields[5].label"
              :tooltip="texts.settings.fields[5].tooltip"
            />
            <div class="settings-row no-mt">
              <DeployerDeployNetworkEnvironment
                :icon="props?.whiteLabel.network_testnet_chain_icon"
                :label="props?.whiteLabel.network_testnet_chain_name"
                :text="props?.whiteLabel.network_testnet_text"
                colors="gray"
                :is-active="oracleValues.networkEnvironment === 'testnet'"
                @click.prevent="
                  switchToChain(
                    props?.whiteLabel.network_testnet_chain_id,
                    'testnet',
                    props?.whiteLabel.network_testnet_rpc_url
                  )
                "
              />

              <DeployerDeployNetworkEnvironment
                :icon="props?.whiteLabel.network_mainnet_chain_icon"
                :label="props?.whiteLabel.network_mainnet_chain_name"
                :text="props?.whiteLabel.network_mainnet_text"
                :is-active="oracleValues.networkEnvironment === 'mainnet'"
                @click.prevent="
                  switchToChain(
                    props?.whiteLabel.network_mainnet_chain_id,
                    'mainnet',
                    props?.whiteLabel.network_mainnet_rpc_url
                  )
                "
              />
            </div>
          </template>
        </DeployerUiBox>
        <DeployerUiBox
          :box-title="texts.additional.title"
          :box-text="texts.additional.text"
        >
          <div class="settings-row">
            <DeployerUiSwitchBox
              :box-title="texts.additional.boxes[0].title"
              :box-text="texts.additional.boxes[0].text"
              :is-active="oracleValues.chainlinkAdapter"
              @click.stop="
                oracleValues.chainlinkAdapter = !oracleValues.chainlinkAdapter
              "
            >
              <DeployerUiInfoText
                :text="texts.additional.boxes[0].info"
                class="sb-info-text"
              />
            </DeployerUiSwitchBox>
            <DeployerUiSwitchBox
              :box-title="texts.additional.boxes[1].title"
              :box-text="texts.additional.boxes[1].text"
              :is-active="oracleValues.prefillOracle"
              @click.stop="
                oracleValues.prefillOracle = !oracleValues.prefillOracle
              "
            >
              <DeployerUiFormGasInput
                v-model="oracleValues.prefillValue"
                :currency="
                  whiteLabel?.prefill_oracle_currency ||
                  oracleValues.prefillCurrency
                "
                :icon="
                  whiteLabel?.prefill_oracle_icon ||
                  'https://cms3.diadata.org/wp-content/uploads/2024/05/Base-network-icon.svg'
                "
                @click.stop=""
              />
            </DeployerUiSwitchBox>
          </div>
        </DeployerUiBox>
      </div>
    </div>
    <DeployerDeployProgress
      v-if="
        currentDeployStep !== null && currentDeployStep !== 0 && !deployError
      "
      :deploy-max-steps="deployMaxSteps"
      :current-step="currentDeployStep"
    />
    <DeployerDeployDone v-if="currentDeployStep === 1000" />
    <DeployerDeployError
      v-if="deployError !== null"
      @try-again="tryAgain"
      @close="closeModal"
    />
    <div v-if="!deployerStore.summaryMobileOpened" class="review-wrapper">
      <button
        type="button"
        class="btn cta w100 review-btn"
        :disabled="!canDeploy"
        @click.prevent="deployerStore.summaryMobileOpened = true"
      >
        {{ texts.sidebar.review_oracle }}
      </button>
    </div>
  </div>
</template>

<script setup>
import {
  deployContract,
  getChains,
  waitForTransactionReceipt,
  writeContract
} from '@wagmi/core'
import DIAOracleV2 from 'dia-oracle'

import AddOracleSvg from '~/layers/base/assets/icons/add-oracle.svg?raw'
import RpcSvg from '~/layers/base/assets/icons/rpc.svg?raw'
import NetworkSvg from '~/layers/base/assets/icons/network.svg?raw'

import X from '~/layers/base/assets/icons/x.svg?raw'
import Check from '~/layers/base/assets/icons/check.svg?raw'
import Loading from '~/layers/base/assets/icons/loading.svg?raw'
import { switchChain } from '@wagmi/core'
import { toHex } from 'viem'

const config = useRuntimeConfig()
const props = defineProps({
  whiteLabel: Object
})

const deployerStore = useDeployerStore()
const newWalletStore = useNewWalletStore()
const textsStore = useTextsStore()
const accountStore = useAccountStore()
const texts = computed(() => {
  console.log('textsStore', textsStore)
  console.log('props?.whiteLabel', props?.whiteLabel)
  return textsStore.deploy
})

const showFeedsErrors = ref(false)

const feedsAvailable = computed(() => {
  return (
    accountStore.getAccountNumberOfFeeds.maximum -
    accountStore.getAccountNumberOfFeeds.deployed -
    oracleValues.value.selectedAssets.length
  )
})

const supportedChainIds = computed(() => {
  return props.whiteLabel?.chains_available?.split(',').map((id) => Number(id))
})

const chainRPCValidation = reactive({
  isValid: true,
  messages: [],
  loading: false
})

function validateRpcUrl(rpcUrl) {
  if (typeof rpcUrl !== 'string') {
    throw new Error('RPC URL needs to be a string')
  }

  try {
    const parsedUrl = new URL(rpcUrl)

    if (!['http:', 'https:', 'wss'].includes(parsedUrl.protocol)) {
      throw new Error('RPC URL must use http or https protocol')
    }

    if (!parsedUrl.hostname) {
      throw new Error('RPC URL must have a hostname')
    }
  } catch (error) {
    throw new Error('Wrong RPC format: ' + error.message)
  }

  return true
}

async function validateChainRPC() {
  chainRPCValidation.loading = true
  chainRPCValidation.isValid = true
  chainRPCValidation.messages = []

  if (typeof Number(oracleValues.value.chainId) !== 'number') {
    chainRPCValidation.isValid = false
    chainRPCValidation.messages = [
      'Enter a valid Chain ID. Only numbers allowed.'
    ]

    chainRPCValidation.loading = false

    return
  }

  if (
    !supportedChainIds.value.some(
      (id) => id === Number(oracleValues.value.chainId)
    )
  ) {
    chainRPCValidation.isValid = false
    chainRPCValidation.messages = [
      ...chainRPCValidation.messages,
      'This chain is currently not supported.'
    ]

    chainRPCValidation.loading = false

    return
  }

  try {
    validateRpcUrl(oracleValues.value.rpc_url)
  } catch (error) {
    chainRPCValidation.isValid = false
    chainRPCValidation.messages = [
      ...chainRPCValidation.messages,
      'Enter a valid RPC URL address.'
    ]

    chainRPCValidation.loading = false

    return
  }

  try {
    const response = await fetch(oracleValues.value.rpc_url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        jsonrpc: '2.0',
        method: 'eth_chainId',
        id: 1,
        params: []
      })
    })

    const { result } = await response.json()

    if (parseInt(result, 16) !== parseInt(oracleValues.value.chainId, 10)) {
      chainRPCValidation.isValid = false
      chainRPCValidation.messages = [
        ...chainRPCValidation.messages,
        'Chain ID and RPC URL don’t match.'
      ]

      chainRPCValidation.loading = false

      return
    }

    chainRPCValidation.isValid = true
    chainRPCValidation.messages = ['Correct network settings.']

    chainRPCValidation.loading = false
  } catch (error) {
    console.error('Error validating RPC and chain ID', error)
  }
}

const debouncedValidateChainRPC = useDebounceFn(() => validateChainRPC(), 500)

const paymentStatus = ref(null)

const isPaymentProcessed = computed(() => {
  return paymentStatus.value?.event === 'TransferProcessed'
})

async function checkPaymentStatus(walletAddress) {
  const { data, error } = await useFetch(
    `${config.public.oracleApiBaseURL}/oraclebuilder-prod/paymentStatus?wallet=${walletAddress}`
  )

  if (error.value) {
    console.error('Error checking payment status', error)
    return
  }

  paymentStatus.value = data.value
}

function handleFocusChange() {
  if (!newWalletStore?.account?.address) return

  checkPaymentStatus(newWalletStore.account.address)
  accountStore.viewDashboard()
}

onMounted(async () => {
  if (newWalletStore?.account?.address)
    await checkPaymentStatus(newWalletStore?.account?.address)

  document.addEventListener('visibilitychange', handleFocusChange)
})

onUnmounted(() => {
  document.removeEventListener('visibilitychange', handleFocusChange)
})

watch(
  () => newWalletStore?.account?.address,
  async (newAddress) => {
    if (newAddress) {
      await checkPaymentStatus(newAddress)
    }
  }
)

watch(
  () => accountStore?.account,
  async (account) => {
    if (account) {
      await checkPaymentStatus(newWalletStore.account.address)
    }
  }
)

const oracleValues = ref({
  name: '',
  chainId: Number(props?.whiteLabel?.network_raas_chain_id) || '',
  rpc_url: props?.whiteLabel?.network_raas_rpc || '',
  heartbeat: '',
  heartbeatUnit: 'sec',
  networkEnvironment: '',
  selectedAssets: [],
  deviation: '',
  chainlinkAdapter: false,
  prefillOracle: false,
  prefillValue: 0,
  prefillCurrency: props?.whiteLabel?.prefill_oracle_currency
})

const currentDeployStep = ref(null)
const deployMaxSteps = ref(4)
// 1000 - done

const errors = ref({})

const showChainRpc = computed(() => {
  return props.whiteLabel?.network === 'raas'
})

const validateTime = () => {
  const timeInSec = useTimeInSec(
    oracleValues.value.heartbeat,
    oracleValues.value.heartbeatUnit
  )
  if (timeInSec > 24 * 60 * 60) {
    errors.value.heartbeat = 'Maximum value is 24 hours'
  } else if (timeInSec < 120) {
    errors.value.heartbeat = 'Minimum value is 120 seconds'
  } else {
    errors.value.heartbeat = ''
  }
}
const validateDeviation = () => {
  if (oracleValues.value.deviation > 100) {
    errors.value.deviation = 'Maximum value is 100'
  } else if (oracleValues.value.deviation < 0.1) {
    errors.value.deviation = 'Minimum value is 0.1'
  } else {
    errors.value.deviation = ''
  }
}

const hasAllValues = computed(() => {
  // if (
  //   oracleValues.value.chainId &&
  //   oracleValues.value.rpc_url &&
  //   oracleValues.value.selectedAssets.length > 0 &&
  //   oracleValues.value.heartbeat &&
  //   oracleValues.value.deviation &&
  //   oracleValues.value.networkEnvironment
  // ) {
  //   return true
  // }
  if (oracleValues.value.selectedAssets.length > 0) {
    return true
  }
})

watch(() => oracleValues.value.heartbeat, validateTime)
watch(() => oracleValues.value.heartbeatUnit, validateTime)
watch(() => oracleValues.value.deviation, validateDeviation)
watch(
  () => oracleValues.value.prefillValue,
  () => {
    if (oracleValues.value.prefillValue < 0) {
      oracleValues.value.prefillValue = 0
    } else if (oracleValues.value.prefillValue > 0) {
      oracleValues.value.prefillOracle = true
    }
  }
)

watch(
  () => oracleValues.value.prefillOracle,
  (newVal) => {
    if (!newVal) {
      oracleValues.value.prefillValue = 0
    }
  }
)

watch(
  oracleValues,
  () => {
    deployerStore.selectedAssetsCount =
      oracleValues.value.selectedAssets?.length

    if (oracleValues.value.chainlinkAdapter) {
      deployMaxSteps.value = 4 + oracleValues.value.selectedAssets?.length
    } else {
      deployMaxSteps.value = 4
    }
  },
  { deep: true }
)

const canDeploy = computed(() => {
  return (
    Object.values(errors.value).every((val) => val === '') &&
    hasAllValues.value &&
    feedsAvailable.value >= 0
  )
})

function payDeployment() {
  const url = `${deployerStore.selectedPlan.paymentUrl}?refId=${newWalletStore.account.address}`

  navigateTo(url, {
    external: true,
    open: {
      target: '_blank'
    }
  })
}

const getHeartbeatInSeconds = (heartbeat, unit) => {
  switch (unit) {
    case 'sec':
      return heartbeat
    case 'min':
      return heartbeat * 60
    case 'hr':
      return heartbeat * 60 * 60
  }
}

const deployOracleData = ref({
  contractHash: null,
  contractAddress: null,
  transactionUrl: null,
  registerOracleData: null,
  feederAddress: null
})

const deployError = ref(null)

const switchToChain = async (chainid, type, rpcUrl) => {
  chainid = Number(chainid)
  oracleValues.value.networkEnvironment = type

  console.log(
    'oracleValues.networkEnvironment',
    oracleValues.value.networkEnvironment
  )
  console.log('seitching chain', chainid)
  try {
    // Call switchChain and await its completion
    await switchChain(newWalletStore.wagmiConfig, {
      addEthereumChainParameter: {
        chainId: toHex(chainid)
      },
      chainId: chainid
    })

    oracleValues.value.chainId = chainid
    oracleValues.value.rpc_url = rpcUrl

    console.log('Successfully switched to chain')
  } catch (error) {
    console.error('Error switching chain:', error)
  }
}

const deployOracle = async () => {
  const contractAddress = localStorage.getItem('contractAddress')

  if (!contractAddress) {
    currentDeployStep.value = 1
    try {
      await deployFirstStep()
    } catch (err) {
      deployError.value = true
      return
    }

    await awaitForTransaction(deployOracleData.value.contractHash)
    if (deployError.value) {
      return
    }
  }

  currentDeployStep.value = 2
  await saveOracle()
  if (deployError.value) {
    return
  }

  currentDeployStep.value = 3

  console.log(
    'deployOracleData.value.registerOracleData',
    deployOracleData.value
  )

  deployOracleData.value.feederAddress = await startfeeder(
    deployOracleData.value.registerOracleData.feederID
  )
  if (deployError.value) {
    return
  }

  currentDeployStep.value = 4
  await assignOwnership()
  if (deployError.value) {
    return
  }

  currentDeployStep.value = 1000
}

const deployFirstStep = async () => {
  if (canDeploy.value) {
    currentDeployStep.value = 1

    const currentChainId = newWalletStore.account.chainId

    const { abi, bytecode } = DIAOracleV2

    const deployConfig = {
      abi: abi,
      account: newWalletStore.account.address,
      bytecode: bytecode
    }

    if (currentChainId === 11155111 || currentChainId === 5) {
      deployConfig.gas = 1081872
    }
    const hash = await deployContract(newWalletStore.wagmiConfig, {
      ...deployConfig
    })

    deployOracleData.value.contractHash = hash

    return hash
  }
}

const awaitForTransaction = async (hash) => {
  try {
    const chainId = newWalletStore.account.chainId
    const chains = getChains(newWalletStore.wagmiConfig)

    const transaction = await waitForTransactionReceipt(
      newWalletStore.wagmiConfig,
      {
        chainId,
        hash,
        pollingInterval: 2_000
      }
    )

    const currentChain = chains.find(
      (chain) => chain.id === newWalletStore.account.chainId
    )

    deployOracleData.value.contractAddress = transaction.contractAddress
    localStorage.setItem('contractAddress', transaction.contractAddress)
    deployOracleData.value.contractAddress = transaction.contractAddress
    if (currentChain.blockExplorers && currentChain.blockExplorers.default) {
      deployOracleData.value.transactionUrl = `${currentChain.blockExplorers.default.url}/tx/${transaction.contractAddress}`
    }

    return transaction
  } catch (error) {
    console.error('Error waiting for transaction', error)
    deployError.value = true
  }
}

const startfeeder = async (feederId) => {
  const loggedInAddress = newWalletStore.account.address
  const chainId = newWalletStore.account.chainId

  const signedData = await newWalletStore.signData(
    chainId,
    config.public.startFeederMessage,
    loggedInAddress
  )

  const formdata = new FormData()

  formdata.append('feederID', feederId)
  formdata.append('creator', loggedInAddress)
  formdata.append('chainID', chainId)

  const requestOptions = {
    method: 'POST',
    body: formdata,
    redirect: 'follow',
    headers: {
      Authorization: `Bearer ${signedData}`
    }
  }

  try {
    const res = await fetch(
      `${config.public.oracleApiBaseURL}/oraclebuilder-prod/oracle/deploy`,
      requestOptions
    )
    const d = await res.json()
    return d.publicKey
  } catch (err) {
    console.error(err)
    throw new Error('Failed to approve')
  }
}

const saveOracle = async () => {
  const chainId = newWalletStore.account.chainId

  const signedData = await newWalletStore.signData(
    chainId,
    config.public.createUpdateOracleMessage,
    deployOracleData.value.contractAddress
  )

  if (!signedData) {
    console.error('Error signing data - address verification')
  }

  try {
    deployOracleData.value.registerOracleData = await registerOracle({
      name: oracleValues.value.name,
      oracleAddress: deployOracleData.value.contractAddress,
      chainId,
      creator: newWalletStore.account.address,
      signedData,
      symbols: oracleValues.value.selectedAssets
        .map((asset) => `${asset.blockchain}-${asset.address}-${asset.symbol}`)
        .join(','),
      frequency: getHeartbeatInSeconds(
        oracleValues.value.heartbeat,
        oracleValues.value.heartbeatUnit
      ),
      deviationPermille: oracleValues.value.deviation,
      feederID: ''
    })
  } catch (error) {
    deployError.value = true
    console.error(error)
  }
}

const registerOracle = async (request) => {
  const formdata = new FormData()
  formdata.append('oracleaddress', request.oracleAddress)
  formdata.append('chainID', request.chainId)
  formdata.append('creator', request.creator)
  formdata.append('signeddata', request.signedData)
  formdata.append('symbols', request.symbols)
  formdata.append('sleepseconds', '')
  formdata.append('feederID', request.feederID)
  formdata.append('name', request.name)

  if (request.deviationPermille > 0) {
    formdata.append('mandatoryfrequency', request.frequency)
    formdata.append('frequency', 0)
  } else {
    formdata.append('mandatoryfrequency', 0)
    formdata.append('frequency', request.frequency)
    formdata.append('name', request.name)
  }

  const fs = []
  oracleValues.value.selectedAssets.forEach((asset) => {
    const ep = []
    console.log('asset', asset)
    asset.exchanges.forEach((exchange) => {
      ep.push({ Exchange: exchange.Name, Pairs: exchange.Pairs })
    })

    const fsItem = {
      Methodology: 'vwap',
      Symbol: `${asset.symbol.split('/')[0]}-${asset.address}`,
      FeedSelection: [
        {
          Address: asset.address,
          Blockchain: asset.blockchain,
          Exchangepairs: ep
        }
      ]
    }
    fs.push(fsItem)
  })

  const chains = getChains(newWalletStore.wagmiConfig)

  console.log('getChains', chains)
  const currentChain = chains.find(
    (chain) => chain.id === newWalletStore.account.chainId
  )

  console.log('currentChain', currentChain)

  //TODO only for new chains
  if (
    currentChain &&
    currentChain.rpcUrls &&
    currentChain.rpcUrls.default &&
    currentChain.rpcUrls.default.http
  ) {
    if (currentChain.rpcUrls.default.http.length > 0) {
      console.log(
        'currentChain.rpcUrls.default.length[0]',
        currentChain.rpcUrls.default.http[0]
      )
      formdata.append('blockchainnode', currentChain.rpcUrls.default.http[0])
    }
  }

  const feedsel = JSON.stringify(fs).replace(/\\n/g, '').replace(/\//g, '-')

  formdata.append('feedselection', feedsel)

  formdata.append('deviationpermille', request.deviationPermille)

  const requestOptions = {
    method: 'POST',
    body: formdata,
    redirect: 'follow',
    headers: {
      Authorization: `Bearer ${request.signedData}`
    }
  }

  try {
    const res = await fetch(
      `${config.public.oracleApiBaseURL}/oraclebuilder-prod/oracle/feeder`,
      requestOptions
    )
    const d = await res.json()

    localStorage.removeItem('contractAddress')

    return d
  } catch (err) {
    console.error(err)
    deployError.value = true
    throw new Error('Failed to create and register oracle')
  }
}

const assignOwnership = async () => {
  try {
    const chains = getChains(newWalletStore.wagmiConfig)

    console.log('getChains', chains)
    const currentChain = chains.find(
      (chain) => chain.id === newWalletStore.account.chainId
    )
    console.log(
      'deployOracleData.value.feederAddress',
      deployOracleData.value.feederAddress
    )

    const tx = await writeContract(newWalletStore.wagmiConfig, {
      abi: DIAOracleV2.abi,
      address: deployOracleData.value.contractAddress,
      functionName: 'changeOwner',
      args: [deployOracleData.value.feederAddress],
      chainId: currentChain.id
    })

    try {
      await waitForTransactionReceipt(newWalletStore.wagmiConfig, {
        hash: tx
      })
    } catch (err) {
      console.log('waitForTransactionReceipt err', err)
    }

    if (currentChain.blockExplorers && currentChain.blockExplorers.default) {
      deployOracleData.value.oracleExplorerUrl = `${currentChain.blockExplorers.default.url}/tx/${tx}`
    }
  } catch (error) {
    console.error('assignOwnership', error)
    deployError.value = true
  }
}

const removeFeed = (feed) => {
  oracleValues.value.selectedAssets = oracleValues.value.selectedAssets.filter(
    (asset) => asset !== feed
  )
}

const clearFeed = () => {
  oracleValues.value.selectedAssets = []
}

const tryAgain = () => {
  deployError.value = null

  deployOracle()
}

const closeModal = () => {
  currentDeployStep.value = 0
  deployError.value = null
}
</script>

<style lang="scss" scoped>
.chain-validation p {
  display: flex;
  align-items: center;
  margin-top: 0;
  margin-bottom: 5px;
  color: #368f36;
  font-size: 1.5rem;
  margin-top: -25px;
}

.chain-validation .icon {
  margin-top: -3px;
  margin-right: 5px;
}

.chain-validation p.invalid {
  color: #e71313;
}

.chain-validation i {
  margin-right: 10px;
}

.valid-icon {
  color: #368f36;
}

.invalid-icon {
  color: #e71313;
}

.invalid {
  color: #e71313;
}

.deploy-wrapper {
  margin-top: 30px;
  display: flex;
  align-items: flex-start;
  gap: 40px;
}

.sidebar {
  border-radius: 10px;
  border: 1px solid var(--Block-stroke, rgba(0, 0, 0, 0.1));
  background: var(--Block-bg, #fff);
  box-shadow: 0px 0px 10px 0px rgba(0, 0, 0, 0.1);
  width: 330px;
  position: sticky;
  top: 70px;

  @media (max-width: 1023px) {
    position: fixed;
    background: var(--Page-bg, #fff);
    z-index: 99;
    left: 0;
    right: 0;
    top: 100dvh;
    width: 100%;
    height: 100dvh;
    transition: top 0.3s;
    border-radius: 0;
    border: 0;
    &.opened {
      top: 0;
      overflow-y: auto;
    }
  }
}

.page-title {
  :deep(svg) {
    width: 24px;
    height: 24px;
    path {
      stroke-width: 2px;
    }
  }
}

.deploy {
  flex: 1 1 0;

  display: flex;
  flex-direction: column;
  gap: 15px;

  @media (min-width: 1024px) {
    gap: 30px;
  }
}

hr {
  border: 0;
  border-top: 1px solid var(--bubble-10, rgba(0, 0, 0, 0.1));
  margin-block: 30px;
}

.settings-row {
  display: grid;
  gap: 10px;
  margin-block: 30px;

  @media (min-width: 850px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 20px;
  }

  &.no-mt {
    margin-top: 0;
  }
}

.names-row {
  display: grid;
  gap: 20px;
  margin-bottom: 10px;

  @media (min-width: 850px) {
    grid-template-columns: 67% auto;
    gap: 30px;
  }

  :deep(.field) {
    height: 42px;
  }

  &.no-mt {
    margin-top: 0;
  }
}

.sb-info-text {
  margin-top: 40px;
}

.review-wrapper {
  position: fixed;
  bottom: 57px;
  z-index: 99;
  left: 0;
  right: 0;
  padding: 10px;
  border-top: 1px solid var(--bubble-10, rgba(0, 0, 0, 0.1));
  background: rgba(255, 255, 255, 0.1);

  backdrop-filter: blur(40px);

  @media (min-width: 1024px) {
    display: none;
  }
}
</style>
